/**
 * Figured bass, as MusicXML says it.
 *
 * MusicXML gives a chord symbol an `<offset>` and gives a figure none. A `<figured-bass>`
 * stands immediately before the note it belongs to and begins where that note begins, and
 * the way a file says "the figures change under this held bass" is `<duration>`: several
 * `<figured-bass>` elements before the one note, each lasting as long as its own duration
 * says. That is exactly the shape of continuo writing and it is what is built here.
 *
 * A figure that begins after its note does is written with a blank stack in front of it --
 * `<figure/>` with a duration, which is legal and draws nothing -- rather than being pulled
 * back to the note head. A figure drawn under the wrong half of a semibreve is a wrong
 * figure, and a reader that ignores the blank loses nothing it would otherwise have had.
 *
 * Like the chord symbols, the figures are written once per bar from the first voice's
 * timeline, because the reader's cursor is the bar's and not a voice's.
 */

import { markName } from "../document/figures";
import type { Figure, FiguredBass } from "../document/figures";
import type { EventId } from "../document/ids";
import { occupiedTicks } from "../document/query";
import type { PartMeasure, Voice } from "../document/types";
import { el, leaf } from "../xml/write";
import type { XmlNode } from "../xml/write";
import { toDivisions } from "./common";

/** Where each bar's figures go: keyed by the note they are written before. */
export interface FigurePlan {
  readonly byEvent: ReadonlyMap<EventId, readonly XmlNode[]>;
  /** Stacks with no note at or before them, which go at the head of the bar. */
  readonly leading: readonly XmlNode[];
}

const EMPTY: FigurePlan = { byEvent: new Map(), leading: [] };

/** One numeral, with whatever is written around it. */
function figureNode(figure: Figure): XmlNode {
  return el("figure", {}, [
    figure.prefix === null ? null : leaf("prefix", markName(figure.prefix)),
    figure.number === null ? null : leaf("figure-number", figure.number),
    figure.suffix === null ? null : leaf("suffix", markName(figure.suffix)),
    figure.extend ? el("extend", { type: "start" }) : null,
  ]);
}

/** One stack, lasting the given ticks when it does not last its whole note. */
export function figuredBassNode(
  bass: FiguredBass,
  ticks: number | null,
  divisions: number,
): XmlNode {
  return el("figured-bass", { parentheses: bass.parenthesized ? "yes" : null }, [
    ...bass.figures.map(figureNode),
    ticks === null ? null : leaf("duration", toDivisions(ticks, divisions)),
  ]);
}

/** The blank stack that holds a note's first figure back to where it was written. */
function spacerNode(ticks: number, divisions: number): XmlNode {
  return el("figured-bass", {}, [el("figure"), leaf("duration", toDivisions(ticks, divisions))]);
}

/** One note's worth of figures, in the order they are written before it. */
function nodesUnder(
  stacks: readonly FiguredBass[],
  start: number,
  end: number,
  divisions: number,
): readonly XmlNode[] {
  const nodes: XmlNode[] = [];
  if (stacks[0] !== undefined && stacks[0].onset > start) {
    nodes.push(spacerNode(stacks[0].onset - start, divisions));
  }
  for (const [index, stack] of stacks.entries()) {
    const next = stacks[index + 1]?.onset ?? end;
    // A stack that is the only one under its note and starts with it needs no duration:
    // saying nothing means "until the note ends", which is what it does.
    const sole = stacks.length === 1 && stack.onset === start;
    nodes.push(figuredBassNode(stack, sole ? null : next - stack.onset, divisions));
  }
  return nodes;
}

/**
 * Work out where each of a bar's stacks is written.
 *
 * `timeline` is the voice the exporter writes first, which is the one the cursor is on when
 * the stacks are emitted. A stack lands under the last note that began at or before it,
 * which is the note a player reads it against. Anything before the first note of the bar -- which only a file written
 * elsewhere can produce -- goes at the head of the bar with its onset as a duration.
 */
export function figurePlan(
  measure: PartMeasure,
  timeline: Voice | null,
  divisions: number,
  capacity: number,
): FigurePlan {
  if (measure.figures.length === 0) return EMPTY;
  const voice = timeline;
  if (voice === null) return EMPTY;

  const starts: { readonly id: EventId; readonly onset: number; readonly end: number }[] = [];
  let onset = 0;
  for (const event of voice.events) {
    const end = onset + occupiedTicks(event, capacity);
    starts.push({ id: event.id, onset, end });
    onset = end;
  }

  const sorted = [...measure.figures].sort((one, other) => one.onset - other.onset);
  const under = new Map<EventId, FiguredBass[]>();
  const leading: XmlNode[] = [];
  for (const stack of sorted) {
    let host: (typeof starts)[number] | undefined;
    for (const start of starts) {
      if (start.onset > stack.onset) break;
      host = start;
    }
    if (host === undefined) {
      leading.push(figuredBassNode(stack, stack.onset, divisions));
      continue;
    }
    const already = under.get(host.id);
    if (already === undefined) under.set(host.id, [stack]);
    else already.push(stack);
  }

  const byEvent = new Map<EventId, readonly XmlNode[]>();
  for (const [id, stacks] of under) {
    const host = starts.find((start) => start.id === id);
    if (host === undefined) continue;
    byEvent.set(id, nodesUnder(stacks, host.onset, host.end, divisions));
  }
  return { byEvent, leading };
}
